import { Info } from 'lucide-react';
import { useId, useMemo, useState } from 'react';

import { Alert } from '@/design-system/ui/Alert';
import { Button } from '@/design-system/ui/Button';
import { CheckboxMark } from '@/design-system/ui/Checkbox';
import { DateField } from '@/design-system/ui/DateField';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader } from '@/design-system/ui/Dialog';
import { Input } from '@/design-system/ui/Input';
import { SimpleSelect } from '@/design-system/ui/Select';
import { formatDayMonth } from '@escala/domain/dates';
import { NATAL_DISTRICTS } from '@escala/domain/geography';
import { compareNames, formatPhone } from '@escala/domain/members';
import type { AgeRange, VisitorSource } from '@escala/domain/types';
import {
  AGE_RANGES,
  MAX_VISITOR_NOTE,
  VISITOR_SOURCES,
  VISITOR_STAFF_CONSENT,
  type VisitorDraft,
  emptyVisitorDraft,
  visitableServices,
  visitorDraftOf,
  visitorErrors,
} from '@escala/domain/visitors';
import { useStore } from '@/store/AppStore';

const NO_EVENT = '__none';
const NO_ONE = '__none';

/**
 * Ficha de visitante (nova ou edição), registrada pela equipe. Coleta mínima: nome,
 * contato, faixa etária, bairro, como conheceu e quem convidou — sem motivo da visita nem
 * pedido de oração. O consentimento é o de viva voz, marcado por quem atendeu. Fechar com
 * alterações pergunta antes de descartar. `eventId` já preenche o culto (painel do evento).
 */
export default function VisitorDialog({
  visitorId,
  eventId = null,
  onClose,
  onSaved,
}: {
  visitorId?: string;
  eventId?: string | null;
  onClose: () => void;
  onSaved?: (id: string) => void;
}) {
  const { state, actions, today } = useStore();
  const editing = state.visitors.find((v) => v.id === visitorId);
  const event = state.events.find((e) => e.id === eventId);
  const [draft, setDraft] = useState<VisitorDraft>(() =>
    editing ? visitorDraftOf(editing) : emptyVisitorDraft(event?.date ?? today, event?.id ?? null)
  );
  const [initial] = useState(() => JSON.stringify(draft));
  const [showErrors, setShowErrors] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const id = useId();
  const errors = showErrors ? visitorErrors(draft, today) : {};
  const dirty = JSON.stringify(draft) !== initial;

  const set = (patch: Partial<VisitorDraft>) => {
    setDraft((d) => ({ ...d, ...patch }));
    setLeaving(false);
    setError(null);
  };

  const close = () => {
    if (dirty && !leaving) setLeaving(true);
    else onClose();
  };

  const save = () => {
    setShowErrors(true);

    if (Object.keys(visitorErrors(draft, today)).length) return;

    const result = actions.saveVisitor(draft, editing?.id);

    if ('error' in result) {
      setError(result.error);

      return;
    }

    onSaved?.(result.id);
    onClose();
  };

  // As listas dos selects não mudam com o que se digita: montadas uma vez por estado.
  const services = useMemo(() => visitableServices(state.events, today), [state.events, today]);
  const serviceOptions = useMemo(
    () => [
      { value: NO_EVENT, label: 'Outro dia, sem evento' },
      ...services.map((e) => ({
        value: e.id,
        label: `${formatDayMonth(e.date)} · ${e.title} ${e.time}`,
      })),
    ],
    [services]
  );
  const memberOptions = useMemo(
    () => [
      { value: NO_ONE, label: 'Ninguém / não sabe' },
      ...state.members
        .filter((m) => m.inactiveSince === null)
        .sort((a, b) => compareNames(a.name, b.name))
        .map((m) => ({ value: m.id, label: m.name })),
    ],
    [state.members]
  );

  const message = (key: keyof typeof errors) =>
    errors[key] ? (
      <span role="alert" className="text-meta text-status-conflict">
        {errors[key]}
      </span>
    ) : null;

  return (
    <Dialog open onOpenChange={(open) => !open && close()}>
      <DialogContent className="w-[min(600px,calc(100vw-24px))]">
        <DialogHeader
          title={editing ? 'Editar ficha de visitante' : 'Nova ficha de visitante'}
          description="Só o necessário para receber bem quem visitou e, se a pessoa quiser, entrar em contato."
        />
        <DialogBody>
          <Alert tone="info">
            <Info aria-hidden />
            <span>
              A ficha é opcional e não substitui a contagem do evento. Não registre motivo da visita, pedido
              de oração nem outra informação sensível.
            </span>
          </Alert>

          <label className="flex flex-col gap-1.5" htmlFor={`${id}-name`}>
            <span className="text-small font-semibold">
              Nome<span className="text-status-conflict"> *</span>
            </span>
            <Input
              id={`${id}-name`}
              value={draft.name}
              invalid={!!errors.name}
              autoComplete="off"
              onChange={(e) => set({ name: e.target.value })}
            />
            {message('name')}
          </label>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1.5" htmlFor={`${id}-phone`}>
              <span className="text-small font-semibold">Celular (WhatsApp)</span>
              <Input
                id={`${id}-phone`}
                inputMode="tel"
                placeholder="(84) 99876-5432"
                value={formatPhone(draft.phone)}
                invalid={!!errors.phone}
                onChange={(e) => set({ phone: e.target.value.replace(/\D/g, '').slice(0, 11) })}
              />
              {message('phone')}
            </label>
            <label className="flex flex-col gap-1.5" htmlFor={`${id}-email`}>
              <span className="text-small font-semibold">E-mail</span>
              <Input
                id={`${id}-email`}
                type="email"
                value={draft.email}
                invalid={!!errors.email}
                onChange={(e) => set({ email: e.target.value })}
              />
              {message('email')}
            </label>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <label htmlFor={`${id}-age`} className="text-small font-semibold">
                Faixa etária
              </label>
              <SimpleSelect
                id={`${id}-age`}
                value={draft.ageRange}
                onValueChange={(v) => set({ ageRange: v as AgeRange })}
                options={AGE_RANGES}
              />
            </div>
            <label className="flex flex-col gap-1.5" htmlFor={`${id}-district`}>
              <span className="text-small font-semibold">Bairro</span>
              <Input
                id={`${id}-district`}
                list={`${id}-districts`}
                value={draft.district}
                onChange={(e) => set({ district: e.target.value })}
              />
              <datalist id={`${id}-districts`}>
                {NATAL_DISTRICTS.map((d) => (
                  <option key={d.name} value={d.name} />
                ))}
              </datalist>
            </label>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <label htmlFor={`${id}-source`} className="text-small font-semibold">
                Como conheceu a igreja
              </label>
              <SimpleSelect
                id={`${id}-source`}
                value={draft.source}
                onValueChange={(v) => set({ source: v as VisitorSource })}
                options={VISITOR_SOURCES}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor={`${id}-invited`} className="text-small font-semibold">
                Quem convidou
              </label>
              <SimpleSelect
                id={`${id}-invited`}
                value={draft.invitedById ?? NO_ONE}
                onValueChange={(v) => set({ invitedById: v === NO_ONE ? null : v })}
                options={memberOptions}
              />
            </div>
          </div>

          {editing ? null : (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <label htmlFor={`${id}-event`} className="text-small font-semibold">
                  Evento da visita
                </label>
                <SimpleSelect
                  id={`${id}-event`}
                  value={draft.visit.eventId ?? NO_EVENT}
                  onValueChange={(v) => {
                    const chosen = services.find((e) => e.id === v);

                    set({ visit: { date: chosen?.date ?? draft.visit.date, eventId: chosen?.id ?? null } });
                  }}
                  options={serviceOptions}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <span id={`${id}-date`} className="text-small font-semibold">
                  Dia da visita
                </span>
                <DateField
                  aria-labelledby={`${id}-date`}
                  value={draft.visit.date}
                  max={today}
                  disabled={draft.visit.eventId !== null}
                  onChange={(date) => set({ visit: { date, eventId: null } })}
                />
                {message('visit')}
              </div>
            </div>
          )}

          <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
            <legend className="mb-1 text-small font-semibold">O que a pessoa quer</legend>
            {(
              [
                ['wantsContact', 'Quer receber contato da igreja'],
                ['wantsMembership', 'Quer conhecer a membresia (cadastro de membro)'],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                role="checkbox"
                aria-checked={draft[key]}
                onClick={() => set({ [key]: !draft[key] })}
                className="flex items-center gap-2 self-start text-small text-ink"
              >
                <CheckboxMark checked={draft[key]} />
                {label}
              </button>
            ))}
          </fieldset>

          <label className="flex flex-col gap-1.5">
            <span className="text-small font-semibold">Observação (opcional)</span>
            <textarea
              rows={2}
              maxLength={MAX_VISITOR_NOTE}
              value={draft.note}
              onChange={(e) => set({ note: e.target.value })}
              className="w-full min-w-0 resize-y rounded-control border border-line-input bg-white px-3 py-2.5 text-control text-ink placeholder:text-ink-placeholder"
              placeholder="Ex.: veio com a família; prefere contato à noite"
            />
            <span className="text-meta text-ink-subtle">
              Até {MAX_VISITOR_NOTE} caracteres, só o que ajuda no acompanhamento. Nada sensível.
            </span>
            {message('note')}
          </label>

          {editing ? null : (
            <div className="flex flex-col gap-1">
              <button
                type="button"
                role="checkbox"
                aria-checked={draft.consent}
                onClick={() => set({ consent: !draft.consent })}
                className="flex items-start gap-2 text-left text-small font-semibold text-ink"
              >
                <CheckboxMark checked={draft.consent} className="mt-0.5" />
                {VISITOR_STAFF_CONSENT}
              </button>
              {message('consent')}
            </div>
          )}

          {leaving ? (
            <Alert tone="warning" role="alert" className="flex-wrap items-center">
              <span className="min-w-50 flex-1">Descartar as alterações desta ficha?</span>
              <Button size="sm" variant="secondary" onClick={() => setLeaving(false)}>
                Continuar editando
              </Button>
              <Button size="sm" variant="danger" onClick={onClose}>
                Descartar
              </Button>
            </Alert>
          ) : null}

          {error ? <Alert tone="danger">{error}</Alert> : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={close}>
            Cancelar
          </Button>
          <Button onClick={save}>{editing ? 'Salvar ficha' : 'Registrar ficha'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
